import {
  applyDocumentFile,
  parseDocumentFile,
  serializeDocument,
} from '@lakaran/engine';
import type { CadEditor } from './editor/editor';
import { ICONS, type IconName } from './icons';
import { createMenuButton } from './menu';
import { askChoice, askText } from './dialog';
import { blankGraph, type NodeGraph } from '@lakaran/nodes';
import type { GraphStore } from '../nodes/index';
import { kernel } from './workers/pool';
import type { ExportFormat } from './workers/tasks';
import {
  PROJECT_EXTENSION,
  deleteProject,
  listProjects,
  persistenceState,
  projectFileName,
  readProject,
  renameProject,
  requestPersistence,
  storageAvailable,
  uniqueProjectName,
  writeProject,
  type PersistenceState,
} from './storage';
import {
  deleteFolderProject,
  checkConflict,
  ensureWritePermission,
  forgetWorkspace,
  healWorkspaceOnGesture,
  folderLinkingSupported,
  linkFolder,
  listFolderProjects,
  readFolderProject,
  recallWorkspace,
  workspacePermission,
  renameFolderProject,
  writeFolderProject,
  type Baseline,
} from './workspace';
import type { DirectoryHandleLike } from './workspace';
import { detectBrowser, folderLinkExplanation } from './caps';
import { showContextMenu } from './context-menu';

const UNTITLED = 'untitled';

/** Where the current drawing is stored. */
type Target =
  | { kind: 'opfs'; name: string }
  | { kind: 'folder'; root: DirectoryHandleLike; name: string; baseline: Baseline | null }
  | { kind: 'none' };

export interface FileActions {
  open(text: string, name: string): void;
  save(): void;
  saveAs(): void;
  isDirty(): boolean;
  targetName(): string;
}

function fileButton(iconName: IconName, label: string, title: string): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'tool file';
  button.title = title;
  button.setAttribute('aria-label', label);
  const glyph = document.createElement('span');
  glyph.className = 'file-glyph';
  glyph.innerHTML = ICONS[iconName];
  const text = document.createElement('span');
  text.textContent = label;
  button.append(glyph, text);
  return button;
}

function isNodeGraph(value: unknown): value is NodeGraph {
  if (!value || typeof value !== 'object') return false;
  const graph = value as { nodes?: unknown; edges?: unknown };
  return Array.isArray(graph.nodes) && Array.isArray(graph.edges);
}

/** Builds the file in a worker and hands it to the browser (explicit export only). */
async function exportDrawing(
  editor: CadEditor,
  format: ExportFormat,
  name: string,
): Promise<void> {
  const started = performance.now();
  editor.message(`EXPORT  building ${format.toUpperCase()}…`);
  const json = serializeDocument(editor.doc);
  const response = await kernel.run({ kind: 'export', format, json });
  if (response.kind !== 'export' || !response.filename) {
    editor.message('EXPORT  failed');
    return;
  }
  download(response.filename, response.data, response.mime);
  editor.message(`EXPORT  ${response.filename} (${Math.round(performance.now() - started)} ms)`);
  void name;
}

function download(filename: string, data: BlobPart, type: string): void {
  const url = URL.createObjectURL(new Blob([data], { type }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/**
 * Storage wiring for the editor.
 *
 * `Save` writes into the origin private file system (or into the linked PC folder when one is
 * open) — it never downloads. `.lakar` export is the explicit way to move a project to
 * another device. There is no autosave: unsaved work is flagged and confirmed on exit.
 */
export function installFileActions(
  editor: CadEditor,
  host: HTMLElement,
  graph?: GraphStore | null,
): FileActions {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = `${PROJECT_EXTENSION},.json`;
  input.hidden = true;
  host.append(input);

  const fileLabel = document.getElementById('status-file');
  const storeLabel = document.getElementById('status-store');
  const saveButtonRef: { current: HTMLButtonElement | null } = { current: null };

  let target: Target = { kind: 'none' };
  let workspace: DirectoryHandleLike | null = null;
  let dirty = false;
  let persistence: PersistenceState = 'unknown';
  let healArmed = false;
  let healOff: (() => void) | null = null;

  /** The linked folder's name, with a readable fallback when the browser reports none. */
  function workspaceLabel(): string {
    const name = workspace?.name?.trim();
    return name && name.length > 0 ? name : 'linked folder';
  }

  const currentGraph = (): NodeGraph | null => (graph ? graph.current : null);

  // ------------------------------------------------------------- labels

  /** Where Save will land, in words — the one thing that must never be ambiguous. */
  const destinationLabel = (): string => {
    if (target.kind === 'folder') return `${target.name} in the linked folder`;
    if (target.kind === 'opfs') return `${target.name} in browser storage`;
    if (workspace) return `a new file in the linked folder "${workspaceLabel()}"`;
    return 'browser storage as a new project';
  };

  const setDirty = (next: boolean): void => {
    dirty = next;
    const name = targetName();
    document.title = `${dirty ? '● ' : ''}${name} — Lakaran`;
    if (fileLabel) {
      fileLabel.textContent = dirty ? `${name} ●` : name;
      fileLabel.classList.toggle('is-dirty', dirty);
      fileLabel.title = `Saves to ${destinationLabel()}`;
    }
    if (saveButtonRef.current) {
      saveButtonRef.current.classList.toggle('is-dirty', dirty);
      saveButtonRef.current.title = dirty
        ? `Save to ${destinationLabel()} (unsaved changes)`
        : `Save to ${destinationLabel()}`;
    }
  };

  const setStoreLabel = (): void => {
    if (!storeLabel) return;
    const where =
      target.kind === 'folder'
        ? `folder: ${workspace?.name ?? 'linked'}`
        : target.kind === 'opfs'
          ? 'OPFS'
          : 'no file';
    storeLabel.textContent = `${where} · ${persistence === 'persistent' ? 'persistent' : 'best-effort'}`;
    // Save follows the open file; with no file yet it falls back to the linked folder, then OPFS.
    if (target.kind === 'folder') {
      storeLabel.title = `Save writes straight into ${target.name} inside the linked folder "${workspace?.name ?? 'folder'}"`;
    } else if (target.kind === 'opfs') {
      storeLabel.title = `Save writes into browser storage (OPFS) as ${target.name} — export a .lakar to move it to another device`;
    } else if (workspace) {
      storeLabel.title = `Save writes a new .lakar into the linked folder "${workspaceLabel()}"`;
    } else {
      storeLabel.title = `Save writes into browser storage (OPFS). ${persistence === 'persistent' ? 'Browser storage is persistent.' : 'Browser storage is best-effort — export a .lakar copy for safety.'}`;
    }
  };

  function targetName(): string {
    if (target.kind === 'opfs') return target.name;
    if (target.kind === 'folder') return target.name;
    return `${UNTITLED}${PROJECT_EXTENSION}`;
  }

  // -------------------------------------------------------- open / close

  const projectText = (): string => {
    const node = currentGraph();
    return serializeDocument(editor.doc, node ? { graph: node } : undefined);
  };

  /** Parses and installs a project; a malformed file reports an error instead of throwing. */
  const openProject = (text: string, name: string, next: Target): boolean => {
    let project;
    try {
      project = parseDocumentFile(text);
    } catch (error) {
      editor.message(`OPEN  ${name} is not a Lakaran project: ${error instanceof Error ? error.message : String(error)}`);
      return false;
    }

    const count = applyDocumentFile(editor.doc, project);
    if (graph) {
      const saved = project.extra?.graph;
      graph.replace(isNodeGraph(saved) ? saved : blankGraph(), false);
    }
    editor.history.clear();
    editor.selection.clear();
    editor.events.emit('selection', 0);
    editor.zoomExtents();
    target = next;
    setDirty(false);
    setStoreLabel();
    renderTree();
    const nodes = project.extra?.graph ? ' + node graph' : '';
    editor.message(`OPEN  ${count} entities${nodes} from ${name}`);
    return true;
  };

  const openFromText = (text: string, name: string): void => {
    // A file opened from outside the folder/OPFS store becomes a new OPFS project on save.
    openProject(text, name, { kind: 'opfs', name: projectFileName(name) });
  };

  const openStored = async (name: string): Promise<void> => {
    try {
      const text = await readProject(name);
      openProject(text, name, { kind: 'opfs', name });
    } catch (error) {
      editor.message(`OPEN  ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const openFromFolder = async (name: string): Promise<void> => {
    if (!workspace) return;
    try {
      const opened = await readFolderProject(workspace, name);
      openProject(opened.text, name, {
        kind: 'folder',
        root: workspace,
        name: opened.name,
        baseline: { modified: opened.modified, size: opened.size },
      });
    } catch (error) {
      editor.message(`OPEN  ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  // ---------------------------------------------------------------- save

  const saveToTarget = async (): Promise<void> => {
    const text = projectText();

    // A linked folder is the working store: new work is saved into it, not into OPFS.
    if (target.kind === 'none' && workspace) {
      const taken = (await listFolderProjects(workspace)).map((file) => file.name);
      const name = uniqueProjectName(UNTITLED, taken);
      const saved = await writeFolderProject(workspace, name, text);
      target = {
        kind: 'folder',
        root: workspace,
        name: saved.name,
        baseline: { modified: saved.modified, size: saved.size },
      };
      setDirty(false);
      setStoreLabel();
      renderTree();
      editor.message(`SAVE  ${saved.name} → folder "${workspace.name}" (${saved.size} bytes)`);
      return;
    }

    if (target.kind === 'folder') {
      const state = await checkConflict(target.root, target.name, target.baseline);
      if (state === 'modified' || state === 'missing') {
        const choice = await askChoice({
          title: state === 'missing' ? 'File is gone' : 'File changed on disk',
          message:
            state === 'missing'
              ? `${target.name} was moved or deleted outside Lakaran. Your drawing is still in memory.`
              : `${target.name} was modified outside Lakaran since it was opened.`,
          choices:
            state === 'missing'
              ? [
                  { id: 'copy', label: 'Save to a new file', primary: true },
                  { id: 'opfs', label: 'Save to browser storage' },
                  { id: 'cancel', label: 'Cancel' },
                ]
              : [
                  { id: 'reload', label: 'Reload from disk' },
                  { id: 'overwrite', label: 'Overwrite', primary: true },
                  { id: 'copy', label: 'Save a copy' },
                  { id: 'cancel', label: 'Cancel' },
                ],
        });
        if (!choice || choice === 'cancel') return;
        if (choice === 'reload') {
          await openFromFolder(target.name);
          return;
        }
        if (choice === 'opfs') {
          const existing = (await listProjects()).map((project) => project.name);
          const saved = await writeProject(
            uniqueProjectName(target.name.replace(PROJECT_EXTENSION, ''), existing),
            text,
          );
          target = { kind: 'opfs', name: saved.name };
          setDirty(false);
          setStoreLabel();
          renderTree();
          editor.message(`SAVE  ${saved.name} → OPFS (${saved.size} bytes)`);
          return;
        }
        if (choice === 'copy') {
          const name = await askText({
            title: 'Save a copy',
            label: 'File name',
            value: target.name.replace(PROJECT_EXTENSION, '') + ' copy',
          });
          if (!name) return;
          const saved = await writeFolderProject(target.root, projectFileName(name), text);
          target = {
            kind: 'folder',
            root: target.root,
            name: saved.name,
            baseline: { modified: saved.modified, size: saved.size },
          };
          setDirty(false);
          setStoreLabel();
          renderTree();
          editor.message(`SAVE  ${saved.name} (${saved.size} bytes)`);
          return;
        }
        // overwrite falls through
      }

      const saved = await writeFolderProject(target.root, target.name, text);
      target = {
        kind: 'folder',
        root: target.root,
        name: saved.name,
        baseline: { modified: saved.modified, size: saved.size },
      };
      setDirty(false);
      setStoreLabel();
      renderTree();
      editor.message(`SAVE  ${saved.name} (${saved.size} bytes)`);
      return;
    }

    const name = target.kind === 'opfs' ? target.name : projectFileName(UNTITLED);
    const saved = await writeProject(name, text);
    target = { kind: 'opfs', name: saved.name };
    setDirty(false);
    setStoreLabel();
    renderTree();
    editor.message(`SAVE  ${saved.name} → OPFS (${saved.size} bytes)`);
  };

  const save = (): void => {
    void saveToTarget().catch((error: unknown) => {
      editor.message(`SAVE  ${error instanceof Error ? error.message : String(error)}`);
    });
  };

  const saveAs = (): void => {
    void (async () => {
      // A copy stays in the store you are working in: folder files go back to the folder.
      let inFolder = target.kind === 'folder' ? target : null;

      // New work with a linked folder: let the user choose where the copy lands.
      if (!inFolder && workspace) {
        const choice = await askChoice({
          title: 'Save the project where?',
          message: `A .lakar file can go into the linked folder "${workspace.name}" on this PC, or into this browser's storage.`,
          choices: [
            { id: 'folder', label: 'Linked folder', primary: true },
            { id: 'opfs', label: 'Browser storage' },
            { id: 'cancel', label: 'Cancel' },
          ],
        });
        if (!choice || choice === 'cancel') return;
        if (choice === 'folder') {
          inFolder = { kind: 'folder', root: workspace, name: '', baseline: null };
        }
      }
      const existing = inFolder
        ? (await listFolderProjects(inFolder.root)).map((file) => file.name)
        : (await listProjects()).map((project) => project.name);
      const suggestion = uniqueProjectName(
        target.kind === 'none' ? UNTITLED : targetName().replace(PROJECT_EXTENSION, ''),
        existing,
      );
      const name = await askText({
        title: inFolder ? `Save a copy in ${workspace?.name ?? 'the linked folder'}` : 'Save project as',
        label: inFolder ? 'File name' : 'Project name',
        value: suggestion.replace(PROJECT_EXTENSION, ''),
      });
      if (!name) return;
      const finalName = uniqueProjectName(name, existing);

      if (inFolder) {
        const saved = await writeFolderProject(inFolder.root, finalName, projectText());
        target = {
          kind: 'folder',
          root: inFolder.root,
          name: saved.name,
          baseline: { modified: saved.modified, size: saved.size },
        };
        setDirty(false);
        setStoreLabel();
        renderTree();
        editor.message(`SAVE AS  ${saved.name} → folder "${workspace?.name ?? ''}"`);
        return;
      }

      const saved = await writeProject(finalName, projectText());
      target = { kind: 'opfs', name: saved.name };
      setDirty(false);
      setStoreLabel();
      renderTree();
      editor.message(`SAVE AS  ${saved.name} → OPFS`);
    })().catch((error: unknown) => {
      editor.message(`SAVE  ${error instanceof Error ? error.message : String(error)}`);
    });
  };

  // ------------------------------------------------------- file operations

  /** A fresh, empty project written straight into a store (no drawing involved). */
  const blankProjectText = (): string =>
    serializeDocument(editor.doc, graph ? { graph: blankGraph() } : undefined);

  const newInStore = (store: 'opfs' | 'folder'): void => {
    void (async () => {
      const write = async (name: string): Promise<string> => {
        if (store === 'folder' && workspace) {
          const saved = await writeFolderProject(workspace, name, blankProjectText());
          return saved.name;
        }
        const saved = await writeProject(name, blankProjectText());
        return saved.name;
      };

      if (store === 'folder' && !workspace) return;
      const existing =
        store === 'folder' && workspace
          ? (await listFolderProjects(workspace)).map((file) => file.name)
          : (await listProjects()).map((project) => project.name);
      const suggestion = uniqueProjectName('untitled', existing).replace(PROJECT_EXTENSION, '');
      const name = await askText({
        title: store === 'folder' ? `New file in ${workspace?.name ?? 'folder'}` : 'New project',
        label: 'Name',
        value: suggestion,
      });
      if (!name) return;

      const finalName = uniqueProjectName(name, existing);
      const savedName = await write(finalName);
      renderTree();
      editor.message(`NEW  ${savedName} created — press Save to write the current drawing into it`);
    })().catch((error: unknown) => {
      editor.message(`NEW  ${error instanceof Error ? error.message : String(error)}`);
    });
  };

  const duplicateInStore = (store: 'opfs' | 'folder', name: string): void => {
    void (async () => {
      const text =
        store === 'folder' && workspace ? (await readFolderProject(workspace, name)).text : await readProject(name);

      const existing =
        store === 'folder' && workspace
          ? (await listFolderProjects(workspace)).map((file) => file.name)
          : (await listProjects()).map((project) => project.name);
      const stem = name.replace(PROJECT_EXTENSION, '');
      const copyName = uniqueProjectName(`${stem} copy`, existing);

      const savedName =
        store === 'folder' && workspace
          ? (await writeFolderProject(workspace, copyName, text)).name
          : (await writeProject(copyName, text)).name;

      renderTree();
      editor.message(`COPY  ${name} → ${savedName}`);
    })().catch((error: unknown) => {
      editor.message(`COPY  ${error instanceof Error ? error.message : String(error)}`);
    });
  };

  const renameInStore = (store: 'opfs' | 'folder', name: string): void => {
    const where = store === 'folder' ? `the linked folder "${workspace?.name ?? ''}"` : 'browser storage';

    void (async () => {
      const existing =
        store === 'folder' && workspace
          ? (await listFolderProjects(workspace)).map((file) => file.name)
          : (await listProjects()).map((project) => project.name);

      const entered = await askText({
        title: `Rename ${name}`,
        label: 'New name',
        value: name.replace(PROJECT_EXTENSION, ''),
        confirm: 'Rename',
      });
      if (!entered) return;

      const next = projectFileName(entered);
      // Case-only changes are a no-op on every file system we can reach.
      if (next.toLowerCase() === name.toLowerCase()) return;

      const clash = existing.some((entry) => entry.toLowerCase() === next.toLowerCase());
      if (clash) {
        const choice = await askChoice({
          title: `Replace ${next}?`,
          message: `A file called ${next} already exists in ${where}. Renaming ${name} overwrites it — this cannot be undone.`,
          choices: [
            { id: 'replace', label: 'Replace', primary: true },
            { id: 'cancel', label: 'Cancel' },
          ],
        });
        if (choice !== 'replace') return;
      }

      if (store === 'folder' && workspace) {
        const saved = await renameFolderProject(workspace, name, next);
        if (target.kind === 'folder' && target.name === name) {
          target = {
            kind: 'folder',
            root: workspace,
            name: saved.name,
            baseline: { modified: saved.modified, size: saved.size },
          };
        }
      } else {
        const saved = await renameProject(name, next);
        if (target.kind === 'opfs' && target.name === name) target = { kind: 'opfs', name: saved.name };
      }

      setStoreLabel();
      setDirty(dirty);
      renderTree();
      editor.message(`RENAME  ${name} → ${next} in ${where}`);
    })().catch((error: unknown) => {
      editor.message(`RENAME  ${error instanceof Error ? error.message : String(error)}`);
    });
  };

  const deleteFromStore = (store: 'opfs' | 'folder', name: string): void => {
    const where = store === 'folder' ? `the linked folder "${workspace?.name ?? ''}"` : 'browser storage';
    const isCurrent =
      (store === 'folder' && target.kind === 'folder' && target.name === name) ||
      (store === 'opfs' && target.kind === 'opfs' && target.name === name);

    void askChoice({
      title: `Delete ${name}?`,
      message:
        `This deletes the file from ${where} — it cannot be undone.` +
        (isCurrent ? ' The drawing stays open in memory, but it is no longer backed by a file.' : ''),
      choices: [
        { id: 'delete', label: 'Delete', primary: true },
        { id: 'cancel', label: 'Cancel' },
      ],
    }).then(async (choice) => {
      if (choice !== 'delete') return;
      try {
        if (store === 'folder' && workspace) await deleteFolderProject(workspace, name);
        else await deleteProject(name);

        if (isCurrent) {
          // Keep the drawing, drop the link: unsaved work is now honest about being unsaved.
          target = { kind: 'none' };
          setDirty(true);
        }
        setStoreLabel();
        renderTree();
        editor.message(`DELETE  ${name} removed from ${where}`);
      } catch (error) {
        editor.message(`DELETE  ${error instanceof Error ? error.message : String(error)}`);
      }
    });
  };

  /** Right-click menu for a stored project. */
  const fileMenu = (
    event: MouseEvent,
    store: 'opfs' | 'folder',
    name: string,
    open: () => void,
  ): void => {
    event.preventDefault();
    event.stopPropagation();
    showContextMenu(
      event.clientX,
      event.clientY,
      [
        { label: 'Open', hint: 'Enter', run: open },
        { label: 'Rename…', hint: 'F2', run: () => renameInStore(store, name) },
        { label: 'Duplicate', hint: 'Ctrl+D', run: () => duplicateInStore(store, name) },
        { label: 'Delete', hint: 'Del', danger: true, run: () => deleteFromStore(store, name) },
      ],
      name,
    );
  };

  // --------------------------------------------------------------- tree

  const treeHost = document.getElementById('files-list');

  /** Shows/hides the project panel and makes the canvas re-measure its new width. */
  const showPanel = (visible: boolean): void => {
    const panel = document.getElementById('files-panel');
    if (!panel || panel.hidden === !visible) return;
    panel.hidden = !visible;
    if (visible) renderTree();
    window.dispatchEvent(new Event('resize'));
  };

  function renderTree(): void {
    if (!treeHost) return;
    treeHost.replaceChildren();

    const section = (title: string, store: 'opfs' | 'folder'): HTMLElement => {
      const head = document.createElement('div');
      head.className = 'files-head';
      const name = document.createElement('span');
      name.textContent = title;
      const add = document.createElement('button');
      add.type = 'button';
      add.className = 'files-add';
      add.textContent = '+';
      add.title = `New ${PROJECT_EXTENSION} here`;
      add.addEventListener('click', (event) => {
        event.stopPropagation();
        newInStore(store);
      });
      head.append(name, add);
      head.title = 'Right-click for New / Refresh';
      head.addEventListener('contextmenu', (event) => {
        event.preventDefault();
        event.stopPropagation();
        showContextMenu(
          event.clientX,
          event.clientY,
          store === 'folder'
            ? [
                { label: 'New…', hint: '+', run: () => newInStore(store) },
                { label: 'Refresh', hint: '⟳', run: () => renderTree() },
                { label: 'Unlink folder', hint: '×', run: () => void confirmDisconnect() },
              ]
            : [
                { label: 'New…', hint: '+', run: () => newInStore(store) },
                { label: 'Refresh', hint: '⟳', run: () => renderTree() },
              ],
          title,
        );
      });

      const body = document.createElement('div');
      body.className = 'files-group';
      treeHost.append(head, body);
      return body;
    };

    const note = (host: HTMLElement, text: string): HTMLElement => {
      const line = document.createElement('div');
      line.className = 'files-empty';
      line.textContent = text;
      host.append(line);
      return line;
    };

    const addRow = (
      host: HTMLElement,
      name: string,
      meta: string,
      open: () => void,
      current: boolean,
      store: 'opfs' | 'folder',
    ): void => {
      const row = document.createElement('button');
      row.type = 'button';
      row.className = current ? 'files-row is-current' : 'files-row';
      const label = document.createElement('span');
      label.className = 'files-name';
      label.textContent = name;
      const detail = document.createElement('span');
      detail.className = 'files-meta';
      detail.textContent = meta;
      row.append(label, detail);
      row.addEventListener('click', () => withUnsavedGuard(open, name));
      row.title = 'Click to open · right-click for Rename / Duplicate / Delete · F2 renames, Ctrl+D duplicates';
      row.addEventListener('contextmenu', (event) => fileMenu(event, store, name, open));
      row.addEventListener('keydown', (event) => {
        const key = event.key.toLowerCase();
        // Duplicating a file is Ctrl+D, everywhere. Renaming a focused row is F2.
        if ((event.ctrlKey || event.metaKey) && key === 'd') {
          event.preventDefault();
          event.stopPropagation();
          duplicateInStore(store, name);
          return;
        }
        if (event.key !== 'F2') return;
        event.preventDefault();
        event.stopPropagation();
        renameInStore(store, name);
      });
      host.append(row);
    };

    if (workspace) {
      const folderBody = section(`FOLDER · ${workspaceLabel()}`, 'folder');
      void listFolderProjects(workspace)
        .then((files) => {
          if (files.length === 0) note(folderBody, 'No .lakar files in this folder yet.');
          for (const file of files) {
            addRow(
              folderBody,
              file.name,
              new Date(file.modified).toLocaleString(),
              () => void openFromFolder(file.name),
              target.kind === 'folder' && target.name === file.name,
              'folder',
            );
          }
        })
        .catch((error: unknown) => {
          const line = note(folderBody, 'Folder access expired — ');
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'files-add';
          button.textContent = 'Reconnect';
          button.title = 'Grant access to this folder again — one click, no folder picker';
          button.addEventListener('click', () => void reconnect());
          line.append(button);
          editor.message(`FOLDER  ${error instanceof Error ? error.message : String(error)}`);
        });
    }

    const opfsBody = section(workspace ? 'BROWSER · OPFS' : 'OPFS PROJECTS', 'opfs');
    void listProjects()
      .then((projects) => {
        if (projects.length === 0) {
          note(opfsBody, 'Nothing saved yet — press Save to keep a project in this browser.');
        }
        for (const project of projects) {
          addRow(
            opfsBody,
            project.name,
            `${new Date(project.modified).toLocaleString()} · ${(project.size / 1024).toFixed(1)} kB`,
            () => void openStored(project.name),
            target.kind === 'opfs' && target.name === project.name,
            'opfs',
          );
        }
      })
      .catch(() => {
        note(opfsBody, 'This browser has no private file system (OPFS).');
      });
  }

  // ------------------------------------------------------------ toolbar

  input.addEventListener('change', () => {
    const file = input.files?.[0];
    if (!file) return;
    void file
      .text()
      .then((text) => withUnsavedGuard(() => openFromText(text, file.name), file.name))
      .catch((error: unknown) => {
        editor.message(`OPEN  ${error instanceof Error ? error.message : String(error)}`);
      })
      .finally(() => {
        input.value = '';
      });
  });

  // Dropping a `.lakar` onto the viewport opens it, in any browser.
  const stage = document.getElementById('stage');
  if (stage) {
    stage.addEventListener('dragover', (event) => {
      if (!event.dataTransfer?.types.includes('Files')) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
      stage.classList.add('drop-target');
    });
    stage.addEventListener('dragleave', (event) => {
      if (event.relatedTarget && stage.contains(event.relatedTarget as Node)) return;
      stage.classList.remove('drop-target');
    });
    stage.addEventListener('drop', (event) => {
      stage.classList.remove('drop-target');
      const file = event.dataTransfer?.files?.[0];
      if (!file) return;
      event.preventDefault();
      void file
        .text()
        .then((text) => withUnsavedGuard(() => openFromText(text, file.name), file.name))
        .catch((error: unknown) => {
          editor.message(`OPEN  ${error instanceof Error ? error.message : String(error)}`);
        });
    });
  }

  /**
   * Runs `action` only once unsaved work is dealt with — opening a file must never silently
   * throw the current drawing away.
   */
  const withUnsavedGuard = (action: () => void, what: string): void => {
    if (!dirty) {
      action();
      return;
    }
    void askChoice({
      title: 'Discard changes?',
      message: `The current drawing has unsaved changes. Opening ${what} replaces it.`,
      choices: [
        { id: 'save', label: 'Save first', primary: true },
        { id: 'discard', label: 'Discard' },
        { id: 'cancel', label: 'Cancel' },
      ],
    }).then((choice) => {
      if (choice === 'discard') {
        action();
        return;
      }
      if (choice !== 'save') return;
      void saveToTarget()
        .then(() => {
          // Save As can be cancelled, so continue only when the work really landed in a file.
          if (!dirty) action();
        })
        .catch((error: unknown) => {
          editor.message(`SAVE  ${error instanceof Error ? error.message : String(error)}`);
        });
    });
  };

  const newButton = fileButton('newFile', 'New', 'New project');
  newButton.addEventListener('click', () => {
    if (dirty) {
      void askChoice({
        title: 'Discard changes?',
        message: 'The current drawing has unsaved changes.',
        choices: [
          { id: 'save', label: 'Save first', primary: true },
          { id: 'discard', label: 'Discard' },
          { id: 'cancel', label: 'Cancel' },
        ],
      }).then((choice) => {
        if (choice === 'save') save();
        else if (choice === 'discard') startNew();
      });
      return;
    }
    startNew();
  });

  function startNew(): void {
    editor.newDrawing();
    graph?.replace(blankGraph(), false);
    target = { kind: 'none' };
    setDirty(false);
    setStoreLabel();
    renderTree();
    editor.message('NEW  untitled drawing (press Save to keep it in this browser)');
  }

  const openMenu = createMenuButton(host, {
    icon: 'open',
    label: 'Open',
    title: 'Open a project from browser storage or a linked folder',
    items: () => {
      const items = [
        { label: 'From this browser', hint: 'OPFS', run: () => showPanel(true) },
        {
          label: 'From file…',
          hint: PROJECT_EXTENSION,
          run: () => input.click(),
        },
      ];
      if (folderLinkingSupported()) {
        items.push({
          label: workspace ? `Folder: ${workspace.name}` : 'Link folder…',
          hint: 'PC',
          run: () => {
            if (workspace) void openFromFolderPicker();
            else void linkWorkspace();
          },
        });
      } else {
        // No picker in this engine: say so where the feature would be.
        items.push({
          label: 'Link folder — Edge/Chrome only',
          hint: detectBrowser().name,
          run: () => {
            editor.message(
              `FOLDER  Folder linking needs Edge or Chrome (Chromium); ${detectBrowser().name} has no File System Access API. Projects save to browser storage here.`,
            );
            showPanel(true);
          },
        });
      }
      return items;
    },
  });
  void openMenu;

  const saveButton = fileButton('save', 'Save', 'Save the project');
  saveButtonRef.current = saveButton;
  saveButton.addEventListener('click', () => save());

  const exportMenu = createMenuButton(host, {
    icon: 'export',
    label: 'Export',
    title: 'Export to another format or copy the project to another device',
    items: () => [
      {
        label: PROJECT_EXTENSION,
        hint: 'project file',
        run: () => {
          const name = projectFileName(targetName().replace(PROJECT_EXTENSION, ''));
          download(name, projectText(), 'application/json');
          editor.message(`EXPORT  ${name} (for another device)`);
        },
      },
      { label: 'DXF', hint: 'R12 · mm', run: () => void exportDrawing(editor, 'dxf', targetName()) },
      {
        label: 'DWG',
        hint: 'via DXF',
        run: () => {
          void exportDrawing(editor, 'dxf', targetName());
          editor.message('EXPORT  DWG needs a converter; writing DXF instead.');
        },
      },
      { label: 'PDF', hint: '1:1 vector', run: () => void exportDrawing(editor, 'pdf', targetName()) },
    ],
  });
  void exportMenu;

  // ------------------------------------------------------- folder linking

  async function openFromFolderPicker(): Promise<void> {
    if (!workspace) return;
    const granted = await ensureWritePermission(workspace);
    if (!granted) {
      editor.message('FOLDER  write permission was not granted');
      return;
    }
    renderTree();
  }

  async function linkWorkspace(): Promise<void> {
    try {
      const handle = await linkFolder();
      if (!handle) return;
      workspace = handle;
      // A folder just picked has its grant already: drop any heal armed for the previous one.
      healOff?.();
      healOff = null;
      healArmed = false;
      // The open file keeps its target; new, unsaved work now defaults to this folder.
      syncLinkButton();
      setStoreLabel();
      setDirty(dirty);
      showPanel(true);
      renderTree();
      editor.message(`FOLDER  linked "${handle.name}" — it stays linked until you disconnect`);
    } catch (error) {
      const name = (error as { name?: string }).name;
      if (name === 'AbortError') return;
      editor.message(`FOLDER  ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Re-grant access to the linked folder — one click on the stored handle, never a picker. The link
   * itself is permanent; only Disconnect removes it.
   */
  async function reconnect(): Promise<void> {
    if (!workspace) {
      await linkWorkspace();
      return;
    }
    const handle = workspace;
    const granted = await ensureWritePermission(handle);
    syncLinkButton();
    renderTree();
    editor.message(
      granted
        ? `FOLDER  "${handle.name}" reconnected — choose “Allow on every visit” to skip this click next session`
        : 'FOLDER  access was not granted',
    );
  }

  /** One click (or keypress) anywhere re-asks for the grant, so a stored folder heals itself. */
  function armHeal(): void {
    if (healArmed || !workspace) return;
    healArmed = true;
    const handle = workspace;
    healOff = healWorkspaceOnGesture(handle, (granted) => {
      healArmed = false;
      healOff = null;
      syncLinkButton();
      renderTree();
      if (granted) editor.message(`FOLDER  "${handle.name}" reconnected`);
    });
  }

  /** Ends the link for good: the handle is erased, so no later session recalls the folder. */
  async function disconnect(): Promise<void> {
    healOff?.();
    healOff = null;
    healArmed = false;
    const name = workspace?.name ?? 'folder';
    await forgetWorkspace();
    workspace = null;
    syncLinkButton();
    setStoreLabel();
    setDirty(dirty);
    renderTree();
    editor.message(`FOLDER  disconnected "${name}" — link it again to work in it`);
  }

  async function confirmDisconnect(): Promise<void> {
    const name = workspace?.name;
    if (!name) return;
    const choice = await askChoice({
      title: `Unlink "${name}"?`,
      message: 'This removes the saved folder link from Lakaran. It does not delete or move any files.',
      choices: [
        { id: 'unlink', label: 'Unlink folder', primary: true },
        { id: 'cancel', label: 'Keep linked' },
      ],
    });
    if (choice === 'unlink') await disconnect();
  }

  const folderButton = fileButton('folder', 'Files', 'Projects stored in this browser');
  folderButton.addEventListener('click', () => {
    const panel = document.getElementById('files-panel');
    showPanel(panel?.hidden ?? true);
  });
  folderButton.title = folderLinkingSupported()
    ? 'Projects in this browser — link a folder on this PC to work on files directly'
    : 'Projects in this browser — folder linking needs Edge or Chrome (Chromium only)';

  host.append(newButton, openMenu, saveButton, exportMenu, folderButton);

  // --------------------------------------------------------- panel header

  const linkButton = document.getElementById('files-link') as HTMLButtonElement | null;
  const refreshButton = document.getElementById('files-refresh');
  const unlinkButton = document.getElementById('files-unlink') as HTMLButtonElement | null;
  const closePanel = document.getElementById('files-close');
  if (unlinkButton) unlinkButton.innerHTML = ICONS.unlink;

  const syncLinkButton = (): void => {
    if (unlinkButton) unlinkButton.hidden = !workspace;
    if (!linkButton) return;
    if (!folderLinkingSupported()) {
      // Chromium only — say why, and what to use instead, rather than a dead button.
      linkButton.textContent = 'Folder linking: Edge/Chrome only';
      linkButton.disabled = false;
      linkButton.title = folderLinkExplanation(detectBrowser());
      return;
    }
    linkButton.disabled = false;
    linkButton.textContent = workspace ? `Folder: ${workspaceLabel()}` : 'Link folder…';
    linkButton.title = workspace
      ? 'Linked and kept across sessions — use Unlink to choose another folder'
      : 'Link a folder on this PC to read and write .lakar files directly';
  };

  unlinkButton?.addEventListener('click', () => void confirmDisconnect());
  linkButton?.addEventListener('click', () => {
    if (!folderLinkingSupported()) {
      // Explain instead of doing nothing: the browser, not the app, is the limitation.
      void askChoice({
        title: 'Folder linking needs Edge or Chrome',
        message: `${folderLinkExplanation(detectBrowser())}\n\nProjects save to browser storage here, so nothing is lost — Export ▸ .lakar writes a file wherever you want it.`,
        choices: [
          { id: 'export', label: `Export ${PROJECT_EXTENSION}…`, primary: true },
          { id: 'close', label: 'Got it' },
        ],
      }).then((choice) => {
        if (choice !== 'export') return;
        const name = projectFileName(targetName().replace(PROJECT_EXTENSION, ''));
        download(name, projectText(), 'application/json');
        editor.message(`EXPORT  ${name} (for another device)`);
      });
      return;
    }
    if (workspace) void openFromFolderPicker();
    else void linkWorkspace();
  });
  refreshButton?.addEventListener('click', () => renderTree());
  closePanel?.addEventListener('click', () => showPanel(false));

  // ------------------------------------------------------- unsaved guard

  const markDirty = (): void => {
    if (!dirty) setDirty(true);
  };
  editor.events.on('document', markDirty);
  editor.history.onChange(markDirty);

  window.addEventListener('keydown', (event) => {
    if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 's') return;
    event.preventDefault();
    if (event.shiftKey) saveAs();
    else save();
  });

  window.addEventListener('beforeunload', (event) => {
    if (!dirty) return;
    event.preventDefault();
    event.returnValue = '';
  });

  void (async () => {
    // The folder handle lives in IndexedDB, so the link is recovered whether or not OPFS works.
    workspace = await recallWorkspace();
    syncLinkButton();
    if (workspace && (await workspacePermission(workspace)) !== 'granted') armHeal();
    if (!(await storageAvailable())) {
      setStoreLabel();
      renderTree();
      return;
    }
    persistence = await requestPersistence();
    setStoreLabel();
    renderTree();
  })();

  const status = document.getElementById('status-store');
  if (status) {
    void persistenceState().then((state) => {
      persistence = state;
      setStoreLabel();
    });
  }

  setDirty(false);
  setStoreLabel();
  syncLinkButton();
  renderTree();

  return {
    open: openFromText,
    save,
    saveAs,
    isDirty: () => dirty,
    targetName,
  };
}
